import type { HttpRequest } from '../types/http-request';
import { enrichHttpRequestForMatchReplaceMatching } from './match-replace-preview';

type FetchBodies = (requestId: string) => Promise<{
  requestBody?: string;
  responseBody?: string;
} | null>;

type FetchLargeBody = (bodyRef: string) => Promise<string | null>;

export async function resolveStoredBodyForMatchReplace({
  bodyRef,
  bodyStorageType,
  fetchLargeBody,
  fetchLargeBodyAsBinary,
  isBinary,
}: {
  bodyRef?: string;
  bodyStorageType?: 'inline' | 'file';
  fetchLargeBody: FetchLargeBody;
  fetchLargeBodyAsBinary: FetchLargeBody;
  isBinary: boolean;
}): Promise<string | undefined> {
  if (!bodyRef || bodyStorageType !== 'file') {
    return undefined;
  }

  const loaded = isBinary
    ? await fetchLargeBodyAsBinary(bodyRef)
    : await fetchLargeBody(bodyRef);

  return loaded ?? undefined;
}

export async function resolveRequestForMatchReplace({
  request,
  fetchBodies,
  fetchLargeBody,
  fetchLargeBodyAsBinary,
}: {
  request: HttpRequest;
  fetchBodies: FetchBodies;
  fetchLargeBody: FetchLargeBody;
  fetchLargeBodyAsBinary: FetchLargeBody;
}): Promise<HttpRequest> {
  const syncEnriched = enrichHttpRequestForMatchReplaceMatching(request);
  const needsRequestBody =
    !syncEnriched.body &&
    (!!request.bodyRef || request.bodyStorageType === 'file');
  const needsResponseBody =
    !syncEnriched.responseBody &&
    !!request.status &&
    (!!request.responseBodyRef || request.responseBodyStorageType === 'file');
  const needsDbBodies =
    (!syncEnriched.body &&
      request.bodyStorageType !== 'file' &&
      !request.bodyRef) ||
    (!syncEnriched.responseBody &&
      !!request.status &&
      request.responseBodyStorageType !== 'file' &&
      !request.responseBodyRef);

  if (!needsRequestBody && !needsResponseBody && !needsDbBodies) {
    return syncEnriched;
  }

  const [storedRequestBody, storedResponseBody, dbBodies] = await Promise.all([
    needsRequestBody
      ? resolveStoredBodyForMatchReplace({
          bodyRef: request.bodyRef,
          bodyStorageType: request.bodyStorageType,
          fetchLargeBody,
          fetchLargeBodyAsBinary,
          isBinary: request.bodyContentType === 'binary',
        })
      : Promise.resolve(undefined),
    needsResponseBody
      ? resolveStoredBodyForMatchReplace({
          bodyRef: request.responseBodyRef,
          bodyStorageType: request.responseBodyStorageType,
          fetchLargeBody,
          fetchLargeBodyAsBinary,
          isBinary: request.responseBodyContentType === 'binary',
        })
      : Promise.resolve(undefined),
    needsDbBodies ? fetchBodies(request.id) : Promise.resolve(null),
  ]);

  return enrichHttpRequestForMatchReplaceMatching(request, {
    requestBody: storedRequestBody ?? dbBodies?.requestBody,
    responseBody: storedResponseBody ?? dbBodies?.responseBody,
  });
}
